/**
 * Vector PDF document for the entity performance report (option 1B).
 *
 * `ReportPdfDocument` is a `@react-pdf/renderer` tree — not the web DOM — that
 * replicates the on-screen report's chrome and KPI grid as crisp vector content
 * and embeds each chart as a high-resolution PNG (rasterised from the live
 * Recharts SVG by {@link rasterizeCharts}). The result is a faithful visual
 * replica: identical charts, sharp document text, small file, no screenshot.
 *
 * The styling mirrors `Report.tsx`'s values (the muted-canvas surface, the
 * numbered-section ordinals, the period/issued line, the KPI cards) so the PDF
 * reads as the same document. Layout uses react-pdf's Flexbox subset; colours
 * are the same hex values used in the web report.
 *
 * This component is pure and data-driven — it takes a resolved
 * {@link ReportPdfData} (built by the export hook from the report + chart
 * images) and renders it. It performs no capture or download itself.
 */
import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer"
import montserratRegular from "@fontsource/montserrat/files/montserrat-latin-400-normal.woff"
import montserratMedium from "@fontsource/montserrat/files/montserrat-latin-500-normal.woff"
import montserratSemiBold from "@fontsource/montserrat/files/montserrat-latin-600-normal.woff"
import montserratBold from "@fontsource/montserrat/files/montserrat-latin-700-normal.woff"
import type { ChartImage } from "./rasterizeCharts"

/**
 * Register Montserrat with the PDF renderer so the exported document matches the
 * on-screen report's typeface (the app's global font) rather than falling back
 * to react-pdf's built-in Helvetica.
 *
 * The `.woff` files ship with `@fontsource/montserrat`; Vite resolves each
 * import to a served asset URL the (browser-side) renderer can fetch. Weights
 * are registered under one family so `fontFamily: "Montserrat"` +
 * `fontWeight` (400/500/600/700) resolve to the right face. Registration is
 * idempotent at module scope, so it runs once when the document module loads.
 */
Font.register({
  family: "Montserrat",
  fonts: [
    { src: montserratRegular, fontWeight: 400 },
    { src: montserratMedium, fontWeight: 500 },
    { src: montserratSemiBold, fontWeight: 600 },
    { src: montserratBold, fontWeight: 700 },
  ],
})

/**
 * Disable react-pdf's word hyphenation. Its default hyphenation callback breaks
 * long words with hyphens on wrap, which looks wrong for report labels; returning
 * the whole word keeps them intact.
 */
Font.registerHyphenationCallback((word) => [word])

/** A KPI headline card as shown in the PDF (value pre-formatted by the caller). */
export interface ReportPdfKpi {
  /** Card heading (e.g. "Fuel"). */
  label: string
  /** Formatted value + unit (e.g. "123.4 L" or "—"). */
  value: string
  /** Optional secondary line (e.g. "276/744 h"). */
  secondary?: string | null
  /** Optional pre-formatted trend (e.g. "↑ +8.1%"), as baked by `formatKpiTrend`. */
  trend?: string | null
}

/** A numbered document section: a title and the charts that fall under it. */
export interface ReportPdfSection {
  /** Section title (e.g. "Profile", "Generator runtime"). */
  title: string
  /** Rasterised charts belonging to this section, in order. */
  charts: ChartImage[]
}

/** Everything the PDF document needs, assembled by the export hook. */
export interface ReportPdfData {
  /** Header title (e.g. "Garic SES performance report"). */
  title: string
  /** Period metadata line (e.g. "Weekly · 8 Sep 2026 – 14 Sep 2026 · Issued 14 Sep 2026"). */
  periodLine: string
  /** The diesel-baseline caption shown beneath the period line. */
  caption: string
  /** Headline KPI cards. */
  kpis: ReportPdfKpi[]
  /** Graph sections in document order (KPIs are rendered as their own section). */
  sections: ReportPdfSection[]
}

/* -------------------------------------------------------------------------- */
/* Styles (mirroring Report.tsx colours + spacing)                            */
/* -------------------------------------------------------------------------- */

const COLOR_TEXT = "#1a1a1a"
const COLOR_META = "#545454"
const COLOR_CAPTION = "#767676"
const COLOR_ORDINAL = "#c9c9c9"
const COLOR_SEPARATOR = "#e2e2e2"
const COLOR_CARD_BORDER = "#e6e6e6"

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 28,
    paddingVertical: 28,
    fontSize: 10,
    color: COLOR_TEXT,
    fontFamily: "Montserrat",
  },
  header: {
    marginBottom: 16,
  },
  section: {
    // Space between stacked sections as they flow down the page.
    marginBottom: 14,
  },
  title: {
    fontSize: 20,
    fontFamily: "Montserrat",
    fontWeight: 700,
    marginBottom: 4,
  },
  periodLine: {
    fontSize: 10,
    color: COLOR_META,
  },
  caption: {
    fontSize: 8,
    color: COLOR_CAPTION,
    marginTop: 2,
  },
  sectionHeadingRow: {
    flexDirection: "row",
    alignItems: "baseline",
    marginBottom: 6,
  },
  sectionOrdinal: {
    fontSize: 18,
    fontFamily: "Montserrat",
    fontWeight: 700,
    color: COLOR_ORDINAL,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Montserrat",
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  sectionRule: {
    borderBottomWidth: 1,
    borderBottomColor: COLOR_SEPARATOR,
    marginBottom: 8,
  },
  kpiGrid: {
    // All KPI cards on a single row, sharing the width equally (each card is
    // flex:1 — see kpiCard). `gap` spaces them without per-card margins.
    flexDirection: "row",
    gap: 8,
  },
  kpiCard: {
    // Share the row equally across however many KPIs there are. As a column
    // flex parent whose inner card grows (see kpiCardInner), each card fills
    // the row's height, so a card with a secondary line and one without end up
    // the same height rather than ragged.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    flexDirection: "column",
  },
  kpiCardInner: {
    borderWidth: 1,
    borderColor: COLOR_CARD_BORDER,
    borderRadius: 6,
    padding: 10,
    // Fill the card's full height so every KPI box in a row is the same height
    // regardless of whether it carries a secondary line. `minHeight` keeps a
    // floor for a row where no card has a secondary line.
    flexGrow: 1,
    minHeight: 74,
  },
  kpiLabel: {
    fontSize: 9,
    color: COLOR_META,
    marginBottom: 6,
  },
  kpiValue: {
    // Scaled down from the two-up layout so several KPIs read on one row
    // without overflowing their narrower cells.
    fontSize: 16,
    fontFamily: "Montserrat",
    fontWeight: 700,
  },
  kpiSecondary: {
    fontSize: 9,
    color: COLOR_CAPTION,
    marginTop: 5,
  },
})

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/** Two-decimal-agnostic KPI card matching the web report's Figure card. */
function KpiCard({ kpi }: { kpi: ReportPdfKpi }) {
  return (
    <View style={styles.kpiCard}>
      <View style={styles.kpiCardInner}>
        <Text style={styles.kpiLabel}>{kpi.label}</Text>
        <Text style={styles.kpiValue}>{kpi.value}</Text>
        {kpi.secondary ? <Text style={styles.kpiSecondary}>{kpi.secondary}</Text> : null}
        {kpi.trend ? <Text style={styles.kpiSecondary}>{kpi.trend}</Text> : null}
      </View>
    </View>
  )
}

/** A numbered section heading ("01  Profile") with its underline rule. */
function SectionHeading({ ordinal, title }: { ordinal: string; title: string }) {
  return (
    <>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionOrdinal}>{ordinal}</Text>
        <Text style={styles.sectionTitle}>{title}</Text>
      </View>
      <View style={styles.sectionRule} />
    </>
  )
}

/** A4 portrait page width in PDF points (210mm). Charts size to this width. */
const PAGE_WIDTH_PT = 595.28
/** Uniform page padding in points (mirrors {@link styles.page}). */
const PAGE_PADDING_PT = 28
/** Vertical gutter between stacked chart rows. */
const CHART_ROW_GUTTER_PT = 16

/** Printable content width (page width minus horizontal padding). */
const CONTENT_WIDTH_PT = PAGE_WIDTH_PT - PAGE_PADDING_PT * 2
/**
 * Safety ceiling on a chart's rendered height. The report cards have a roughly
 * proportional plot height set at capture time (see `CHART_PLOT_ASPECT` in
 * rasterizeCharts), so a chart captured at any width lands at a consistent
 * landscape shape well within this ceiling and is drawn undistorted at its true
 * aspect. The cap only engages for a pathologically tall source, scaling the
 * whole image down (never squashing it) so it can't fill a page; it is set high
 * enough that normal charts, lone or paired, are never capped and so keep a
 * uniform font size.
 */
const CHART_MAX_HEIGHT_PT = 420

/**
 * Render one chart at `cellWidth` and its natural aspect ratio, never distorted.
 *
 * The image is drawn at `width × (width × aspect)` so its proportions match the
 * captured card exactly. If that height would exceed {@link CHART_MAX_HEIGHT_PT},
 * the whole image is scaled down proportionally (width reduced to match) instead
 * of clamping the height alone — clamping height while keeping the full width is
 * what squashes charts and makes the ratio look off (worse with more data
 * points, whose captured cards are taller). A scaled-down chart is left-aligned
 * in its cell.
 */
function ChartBlock({ chart, cellWidth }: { chart: ChartImage; cellWidth: number }) {
  const aspect = chart.height > 0 && chart.width > 0 ? chart.height / chart.width : 0.4
  let width = cellWidth
  let height = width * aspect
  if (height > CHART_MAX_HEIGHT_PT) {
    height = CHART_MAX_HEIGHT_PT
    width = height / aspect
  }
  return <Image src={chart.dataUrl} style={{ width, height }} />
}

/** Every chart gets its own full-width row: charts are never placed side by
 * side, so each has room for its heading and header tags on one line. This must
 * stay in sync with the full-width capture in `rasterizeCharts`. */
function toChartRows(charts: ChartImage[]): ChartImage[][] {
  return charts.map((chart) => [chart])
}

/** One full-width chart row. `wrap={false}` keeps a row intact across a page
 * break. */
function ChartRow({ row, isLast }: { row: ChartImage[]; isLast: boolean }) {
  return (
    <View style={{ marginBottom: isLast ? 0 : CHART_ROW_GUTTER_PT }} wrap={false}>
      {row.map((chart, index) => (
        <ChartBlock key={`${chart.title}-${index}`} chart={chart} cellWidth={CONTENT_WIDTH_PT} />
      ))}
    </View>
  )
}

/**
 * A whole section: its numbered heading followed by its charts in two-up rows.
 * The heading is glued to the *first* chart row as one `wrap={false}` unit, so a
 * heading never lands alone at the foot of a page while its charts spill onto
 * the next — the previous layout's main spacing fault. Any remaining rows flow
 * after, breaking between rows only. Rows keep their natural size; a short
 * section simply leaves whitespace below rather than stretching.
 */
function ReportSection({ ordinal, title, charts }: { ordinal: string; title: string; charts: ChartImage[] }) {
  const rows = toChartRows(charts)
  const [firstRow, ...restRows] = rows
  return (
    <View style={styles.section} wrap>
      {/* Heading + first row travel together so the heading can't be orphaned. */}
      <View wrap={false}>
        <SectionHeading ordinal={ordinal} title={title} />
        {firstRow ? <ChartRow row={firstRow} isLast={restRows.length === 0} /> : null}
      </View>
      {restRows.map((row, index) => (
        <ChartRow key={`row-${index + 1}`} row={row} isLast={index === restRows.length - 1} />
      ))}
    </View>
  )
}

/* -------------------------------------------------------------------------- */
/* Document                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Render the report as a vector PDF document. Sections are numbered in the same
 * order as the web report (KPIs first as "Headline", then each graph section).
 * Content flows on a single A4 page stream — `@react-pdf/renderer` paginates
 * automatically as sections fill a page — rather than forcing a new page per
 * section (which left short sections adrift on near-empty pages). Each section
 * heading is glued to its first chart row (`wrap={false}`) so a heading never
 * lands alone at the foot of a page.
 */
export function ReportPdfDocument({ data }: { data: ReportPdfData }) {
  // Sections are numbered in web-report order: the Headline (KPIs) is 01 when
  // present, then each non-empty graph section follows. `nextOrdinal` keeps the
  // ordinals sequential as sections are emitted.
  const hasHeadline = data.kpis.length > 0
  let nextOrdinal = hasHeadline ? 2 : 1
  const graphSections = data.sections
    .filter((section) => section.charts.length > 0)
    .map((section) => ({
      ordinal: String(nextOrdinal++).padStart(2, "0"),
      title: section.title,
      charts: section.charts,
    }))

  return (
    <Document title={data.title}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{data.title}</Text>
          <Text style={styles.periodLine}>{data.periodLine}</Text>
          <Text style={styles.caption}>{data.caption}</Text>
        </View>

        {hasHeadline && (
          <View style={styles.section}>
            <SectionHeading ordinal="01" title="Headline" />
            <View style={styles.kpiGrid}>
              {data.kpis.map((kpi, index) => (
                <KpiCard key={`${kpi.label}-${index}`} kpi={kpi} />
              ))}
            </View>
          </View>
        )}

        {/* Graph sections flow one after another; a new page starts only when a
            section's heading + first chart row don't fit the remaining space. */}
        {graphSections.map((section) => (
          <ReportSection
            key={section.ordinal}
            ordinal={section.ordinal}
            title={section.title}
            charts={section.charts}
          />
        ))}
      </Page>
    </Document>
  )
}

export default ReportPdfDocument
